/**
 * Initializes and manages Splide carousels for cart drawer upsell sections.
 * Handles reinitialization after section morphing while preserving scroll position.
 */

(function() {
  const initializedCarousels = new Set();
  const initializingCarousels = new Set();

  /**
   * Initialize a Splide carousel for a given element.
   * @param {HTMLElement} carousel - The carousel container element.
   */
  const initSplideCarousel = async (carousel) => {
    const carouselId = carousel.id;
    if (!carouselId) return;

    // Re-entrancy guard: prevent duplicate inits racing
    if (initializedCarousels.has(carouselId)) {
      return;
    }
    if (initializingCarousels.has(carouselId)) {
      setTimeout(() => {
        if (!initializedCarousels.has(carouselId)) {
          initializingCarousels.delete(carouselId);
          initSplideCarousel(carousel);
        }
      }, 150);
      return;
    }
    initializingCarousels.add(carouselId);

    let splideElement = /** @type {HTMLElement | null} */ (carousel.querySelector('.splide'));
    if (!splideElement) {
      initializingCarousels.delete(carouselId);
      return;
    }

    // Skip if already initialized and working
    const existingInstance = splideElement ? /** @type {any} */ (splideElement).splide : null;

    if (existingInstance && initializedCarousels.has(carouselId)) {
      if (existingInstance.Components?.Slides) {
        initializingCarousels.delete(carouselId);
        return;
      }
    }

    // Wait for Splide library to be available
    const globalWindow = /** @type {any} */ (window);

    if (typeof globalWindow.__splideReady !== 'undefined') {
      await globalWindow.__splideReady;
    } else {
      await new Promise((resolve) => {
        if (globalWindow.Splide) {
          resolve(undefined);
        } else {
          const checkSplide = setInterval(() => {
            if (globalWindow.Splide) {
              clearInterval(checkSplide);
              resolve(undefined);
            }
          }, 100);
        }
      });
    }

    // Re-query after potential DOM changes
    splideElement = /** @type {HTMLElement | null} */ (carousel.querySelector('.splide'));
    if (!splideElement) {
      initializingCarousels.delete(carouselId);
      return;
    }

    // Clean up existing instance
    if (splideElement && /** @type {any} */ (splideElement).splide) {
      /** @type {any} */ (splideElement).splide.destroy();
      /** @type {any} */ (splideElement).splide = null;
    }

    /**
     * @param {string} variable
     * @returns {string}
     */
    const getComputedSpacing = (variable) => {
      const temp = document.createElement('div');
      temp.style.setProperty('display', 'none');
      temp.style.setProperty('padding', `var(${variable})`);
      document.body.appendChild(temp);
      const computed = getComputedStyle(temp).padding;
      document.body.removeChild(temp);
      return computed;
    };

    const paddingRight = getComputedSpacing('--spacing-l');
    const paddingRightDesktop = getComputedSpacing('--spacing-3xl');

    // Create new Splide instance
    const SplideConstructor = globalWindow.Splide;
    const splide = new SplideConstructor(splideElement, {
      drag: true,
      flickPower: 130,
      flickMaxPages: 1,
      snap: false,
      mediaQuery: 'min',
      speed: 300,
      type: 'slide',
      pagination: false,
      arrows: false,
      gap: '12px',
      fixedWidth: '240px',
      padding: {
        right: paddingRight,
      },
      breakpoints: {
        1024: {
          drag: 'free',
          gap: '16px',
          fixedWidth: '304px',
          padding: {
            right: paddingRightDesktop,
          },
        },
      },
    });

    // Update progress bar and ARIA attributes on scroll
    const progressBar = /** @type {HTMLElement | null} */ (carousel.querySelector('.upsell-carousel__carousel-progress-bar'));
    
    if (progressBar) {
      const updateProgress = () => {
        const controller = splide.Components?.Controller;
        if (!controller) return;

        // Try multiple methods to get current position
        const list = splideElement.querySelector('.splide__list');
        const track = splideElement.querySelector('.splide__track');
        
        let rate = 0;
        const end = controller.getEnd() + 1;
        
        // Method 1: Use splide.index (works for snap mode)
        if (splide.index !== undefined) {
          const current = splide.index + 1;
          rate = end > 0 ? Math.min(current / end, 1) : 0;
        }
        
        // Method 2: Calculate from scroll position (works for free drag mode)
        if (list && track && (splide.state.destroyed === false)) {
          const listWidth = list.scrollWidth;
          const trackWidth = track.clientWidth;
          const scrollLeft = list.scrollLeft || track.scrollLeft || 0;
          const maxScroll = listWidth - trackWidth;
          
          if (maxScroll > 0) {
            const scrollRate = Math.min(scrollLeft / maxScroll, 1);
            // Use scroll-based rate if available, otherwise fall back to index-based
            if (scrollRate > 0 || rate === 0) {
              rate = scrollRate;
            }
          }
        }
        
        const percentage = Math.round(rate * 100);
        progressBar.style.setProperty('width', `${100 * rate}%`);
        progressBar.setAttribute('aria-valuenow', percentage.toString());
      };

      // Update on mount and whenever carousel moves
      splide.on('mounted', () => {
        requestAnimationFrame(() => {
          updateProgress();
        });
      });
      splide.on('move', updateProgress);
      splide.on('moved', updateProgress);
      splide.on('updated', updateProgress);
      
      // For free drag mode, listen to scroll/drag events
      splide.on('scroll', updateProgress);
      splide.on('scrolled', updateProgress);
      splide.on('dragging', updateProgress);
      splide.on('dragged', updateProgress);
    }

    // Prevent reinitialization during user interaction
    splide.on('drag', () => {
      isDragging = true;
    });

    splide.on('dragged', () => {
      if (dragEndTimeout) clearTimeout(dragEndTimeout);
      dragEndTimeout = setTimeout(() => {
        isDragging = false;
      }, 500);
    });

    splide.mount();
    
    initializedCarousels.add(carouselId);
    initializingCarousels.delete(carouselId);
  };

  /**
   * Find and initialize all upsell carousels in the cart drawer.
   */
  const initAllUpsellCarousels = () => {
    const carousels = document.querySelectorAll('.cart-drawer__upsell.upsell-carousel');
    carousels.forEach((carouselElement) => {
      const carousel = /** @type {HTMLElement} */ (carouselElement);
      if (!carousel.id) return;

      const splideElement = /** @type {HTMLElement | null} */ (carousel.querySelector('.splide'));
      const splideInstance = splideElement ? /** @type {any} */ (splideElement).splide : null;

      if (splideInstance?.Components?.Drag?.dragging) {
        return;
      }

      // Check if already initialized AND still valid
      const isInitialized = initializedCarousels.has(carousel.id);
      const isValid = splideInstance?.Components?.Slides;
      
      if (isInitialized && !isValid) {
        // Instance was destroyed (e.g., during morphing), remove from set and reinit
        initializedCarousels.delete(carousel.id);
        initializingCarousels.delete(carousel.id);
      }

      if (!initializedCarousels.has(carousel.id) && !initializingCarousels.has(carousel.id)) {
        initSplideCarousel(carousel);
      }
    });
  };

  let isDragging = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let dragEndTimeout;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let initTimeout;

  /**
   * Debounced initialization to prevent rapid reinitialization.
   */
  const debouncedInit = () => {
    if (isDragging) return;
    if (initTimeout) clearTimeout(initTimeout);
    initTimeout = setTimeout(initAllUpsellCarousels, 100);
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllUpsellCarousels);
  } else {
    initAllUpsellCarousels();
  }

  // Reinitialize after section morphing (upsell subtree preserved via data-skip-subtree-update)
  document.addEventListener('section:rendered', () => {
    setTimeout(debouncedInit, 200);
  });

  document.addEventListener('cart:update', debouncedInit);

  // MutationObserver fallback to detect when carousel is dynamically added
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const elementNode = /** @type {HTMLElement} */ (node);
            if (
              elementNode.classList.contains('cart-drawer__upsell') ||
              elementNode.querySelector('.cart-drawer__upsell')
            ) {
              debouncedInit();
              return;
            }
          }
        }
      }
    }
  });

  // Observe cart drawer for dynamically added carousels
  const observeCartDrawer = () => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
      });

      // Ensure Splide recalculates layout when the drawer opens after being closed
      const onDrawerOpen = () => {
        const carousels = cartDrawer.querySelectorAll('.cart-drawer__upsell.upsell-carousel');
        carousels.forEach((carouselElement) => {
          const carousel = /** @type {HTMLElement} */ (carouselElement);
          const splideElement = /** @type {HTMLElement | null} */ (carousel.querySelector('.splide'));
          const instance = splideElement ? /** @type {any} */ (splideElement).splide : null;

          // Force root visible in case global CSS left it hidden while dialog was closed
          if (splideElement) {
            splideElement.style.visibility = 'visible';
            const rootStyle = getComputedStyle(splideElement);
            if (rootStyle.display === 'none') splideElement.style.display = '';
            if (rootStyle.opacity === '0') splideElement.style.opacity = '';
          }

          if (instance && splideElement) {
            // Refresh layout when opening to fix invisible slides initialized while hidden
            try {
              instance.refresh();
            } catch (error) {
              console.error('[Cart Drawer Upsell] Failed to refresh Splide instance on drawer open.', error);
            }

            // As a safety, unhide core elements if any visibility styles linger
            const list = /** @type {HTMLElement | null} */ (splideElement.querySelector('.splide__list'));
            const slides = splideElement.querySelectorAll('.splide__slide');
            const track = /** @type {HTMLElement | null} */ (splideElement.querySelector('.splide__track'));

            if (list) {
              const listStyle = getComputedStyle(list);
              if (listStyle.visibility === 'hidden') list.style.removeProperty('visibility');
              if (listStyle.opacity === '0') list.style.removeProperty('opacity');
              if (listStyle.display === 'none') list.style.removeProperty('display');
            }

            if (track) {
              const trackStyle = getComputedStyle(track);
              if (trackStyle.visibility === 'hidden') track.style.removeProperty('visibility');
              if (trackStyle.opacity === '0') track.style.removeProperty('opacity');
              if (trackStyle.display === 'none') track.style.removeProperty('display');
            }

            slides.forEach((slide) => {
              const slideElement = /** @type {HTMLElement} */ (slide);
              slideElement.style.removeProperty('visibility');
            });

            // Defer an additional refresh to the next frame to finalize sizes
            requestAnimationFrame(() => {
            try {
              instance.refresh();
            } catch (error) {
              console.error('[Cart Drawer Upsell] Failed to refresh Splide instance on deferred drawer open.', error);
            }
            });
          } else {
            // If no instance exists yet, initialize now that the drawer is visible
            initSplideCarousel(carousel);
          }
        });
      };

      // Listen for the dialog open event on the cart drawer element
      cartDrawer.addEventListener('dialog:open', onDrawerOpen);
    } else {
      setTimeout(observeCartDrawer, 100);
    }
  };

  observeCartDrawer();
})();
